import { useMemo } from "react";
import { useLocation } from "react-router-dom";

// material-ui
import { Theme } from "@mui/material/styles";
import { Box, Stack, Tooltip, Typography, useMediaQuery } from "@mui/material";

// material-ui icons
import VisibilityIcon from "@mui/icons-material/VisibilityRounded";
import CommentIcon from "@mui/icons-material/CommentRounded";
import EditIcon from "@mui/icons-material/EditRoad";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenterRounded";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesomeRounded";
import ForumIcon from "@mui/icons-material/ForumRounded";

// project import
import IconButton from "components/@extended/IconButton";
import useConfig from "hooks/useConfig";
import Profile from "./Profile";
import AudienceMapTitle from "./AudienceMapTitle";
import Localization from "./Localization";
import MobileSection from "./MobileSection";
import Breadcrumbs from "components/@extended/Breadcrumbs";
import navigation from "menu-items";
import EstatesAndMapSelecter from "components/estatesAndMapSelecter";
import ShareData from "components/shareData";
import useAppFunctionalityConfig from "hooks/useAppFunctionalityConfig";
import { MapProjectRoles } from "types/auth";
import useEstatesAndMaps from "hooks/useEstatesAndMaps";
import { useTranslation } from "utils/locales/utilityFunctions";
import { AIChatWidget } from "features/aiChat/aiChatWidget";
import { useCrispSupport } from "features/supportChat/hooks/useCrispSupport";
import useAuth from "hooks/useAuth";
import { checkAIChatAvailability } from "utils/availabilityTools";

// ==============================|| HEADER - CONTENT ||============================== //
const HeaderContent = () => {
  // --- Hooks --- //
  const { aiAssistant, setAIAssistantData } = useAuth();
  const { i18n } = useConfig();
  const { config: appConfig, isAudienceMode } = useAppFunctionalityConfig();
  const { translate } = useTranslation();
  const { selectedMapId, selectedProjectId } = useEstatesAndMaps();
  const location = useLocation();
  const matchesMd = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const matchesSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const matchesXs = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const { supportOpen, closeSupportChat, toggleSupportChat } = useCrispSupport();

  // --- State --- //
  const localization = useMemo(() => <Localization />, [i18n]);

  const permissionIcon = mapPermission(
    appConfig.layout.currentMapRole,
    appConfig.layout.currentProjectRole,
    selectedMapId !== null,
    selectedProjectId !== null,
    translate,
  );

  const showProfile = appConfig.layout.headerProfile !== "hidden";
  const showMapRole = appConfig.layout.headerMapRole !== "hidden";
  const showShareData = appConfig.layout.shareData !== "hidden";
  const showAIChat = checkAIChatAvailability({
    config: appConfig.aiAssistant,
  });
  const showChat = appConfig.help.chat !== "hidden";

  const showPermissionIcon = !matchesSm && showMapRole && (selectedMapId || selectedProjectId);

  const handleToggleAI = () => {
    if (!aiAssistant.openAiChatWindow && supportOpen) {
      closeSupportChat();
    }

    setAIAssistantData("openAiChatWindow", !aiAssistant.openAiChatWindow);
  };

  const handleToggleSupport = () => {
    if (!supportOpen && aiAssistant.openAiChatWindow) {
      setAIAssistantData("openAiChatWindow", false);
    }

    toggleSupportChat();
  };

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", flex: 1, minWidth: 0 }}>
        {/* LEFT */}
        <Box sx={{ display: "flex", alignItems: "center", pl: isAudienceMode ? 0 : 1, pr: 1, flex: 1, minWidth: 0 }}>
          <AudienceMapTitle />
          {!isAudienceMode && !matchesMd && (
            <Breadcrumbs
              navigation={navigation}
              title={false}
              titleBottom={false}
              card={false}
              divider={false}
            />
          )}

          {!isAudienceMode && location.pathname.startsWith("/map-page") && (
            <>
              <Box
                sx={{
                  flex: matchesMd ? "1 1 auto" : "0 1 auto",
                  minWidth: 0,
                  maxWidth: matchesMd ? "calc(100vw - 140px)" : undefined,
                  overflow: "hidden",
                }}
              >
                <EstatesAndMapSelecter parent="map" />
              </Box>

              {showPermissionIcon && (
                <>
                  <Box sx={{ bgcolor: "#D6D6D6", width: "1px", height: "36px", mx: 1 }} />
                  {permissionIcon}
                </>
              )}

              {showShareData && (
                <>
                  <Box sx={{ bgcolor: "#D6D6D6", width: "1px", height: "36px", mx: 1 }} />
                  <ShareData withButton={true} withAudience={true} withPersonSelector={true} />
                </>
              )}
            </>
          )}
        </Box>

        {/* RUGHT */}
        <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
          {showAIChat && (
            <Tooltip title={translate("ai-chat-button")}>
              <div className="PF-ai-chat-button">
                <IconButton
                  sx={{
                    color: aiAssistant.openAiChatWindow ? "primary.main" : "white",
                    bgcolor: aiAssistant.openAiChatWindow ? "primary.lighter" : "primary.light",
                    '&:hover': {
                      bgcolor: "primary.main",
                      color: "white"
                    },
                    mr: 1
                  }}
                  onClick={handleToggleAI}
                >
                  <AutoAwesomeIcon
                    fontSize="small"
                    sx={{
                      ml: -0.3,
                      width: 14,
                      height: 14,
                      color: "inherit"
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "inherit"
                    }}>
                    AI
                  </Typography>
                </IconButton>
              </div>
            </Tooltip>
          )}

          {!matchesXs && (
            <Stack direction={"row"} spacing={1} sx={{ alignItems: "center" }}>
              {showChat && (
                <Tooltip title={translate("chat-button")}>
                  <div className="PF-chat-button">
                    <IconButton
                      color="secondary"
                      variant="light"
                      sx={{
                        color: "text.primary",
                        bgcolor: supportOpen ? "primary.lighter" : "grey.100",
                      }}
                      onClick={handleToggleSupport}
                    >
                      <ForumIcon fontSize="small" color="primary" />
                    </IconButton>
                  </div>
                </Tooltip>
              )}

              {!isAudienceMode && (
                <Box sx={{ bgcolor: "#D6D6D6", width: "1px", height: "36px", flexShrink: 0 }} />
              )}

              {localization}

              {showProfile && (
                <Box sx={{ bgcolor: "#D6D6D6", width: "1px", height: "36px", flexShrink: 0 }} />
              )}

              {showProfile && <Profile />}
            </Stack>
          )}

          {matchesMd && (
            <MobileSection
              showProfile={showProfile}
              showChat={showChat}
              supportOpen={supportOpen}
              toggleSupportChat={handleToggleSupport}
            />
          )}

          {/* <Notification /> */}
        </Box>
      </Box>

      {showAIChat && <AIChatWidget open={aiAssistant.openAiChatWindow} onClose={() => setAIAssistantData("openAiChatWindow", false)} />}
    </>
  );
};

// --- Helper functions --- //
const mapPermission = (
  mapRole: MapProjectRoles,
  projectRole: MapProjectRoles,
  selectedMap: boolean,
  selectedProject: boolean,
  translate: (key: string) => string,
) => {
  const defaultIcon = (
    <Tooltip title={translate("map-role-not-selected-tooltip")}>
      <VisibilityIcon fontSize="small" color="secondary" />
    </Tooltip>
  );

  if (!selectedMap && !selectedProject) return defaultIcon;

  let role = mapRole;
  if (selectedProject) role = projectRole;

  switch (role) {
    case "viewer":
      return (
        <Tooltip title={translate("map-role-viewer-icon-tooltip")}>
          <VisibilityIcon fontSize="small" color="secondary" />
        </Tooltip>
      );

    case "commentator":
      return (
        <Tooltip title={translate("map-role-commentator-icon-tooltip")}>
          <CommentIcon fontSize="small" color="secondary" />
        </Tooltip>
      );

    case "editor":
      return (
        <Tooltip title={translate("map-role-editor-icon-tooltip")}>
          <EditIcon fontSize="small" color="secondary" />
        </Tooltip>
      );

    case "admin":
      return (
        <Tooltip title={translate("map-role-admin-icon-tooltip")}>
          <AdminPanelSettingsIcon fontSize="small" color="secondary" />
        </Tooltip>
      );

    case "buyer":
      return (
        <Tooltip title={translate("map-role-buyer-icon-tooltip")}>
          <BusinessCenterIcon fontSize="small" color="secondary" />
        </Tooltip>
      );

    default:
      return defaultIcon;
  }
};

export default HeaderContent;
